/**
 * Campaign-detail → real-data mapping (pure).
 *
 * The running-campaign detail draws every figure from a real source now:
 *  - the ad-network metrics (Ad Spend, Top Websites' impressions/clicks, the
 *    generated banner assets) come from the Casino Ad Service campaign
 *    (`pages/acquisition/api/casino-ad-service`);
 *  - the casino-conversion figures (Registrations, FTDs, FTD Value, Turnover) come
 *    from the Owners Hub Stats `daily_affiliate_activity` data set, aggregated on the
 *    `campaign` dimension and filtered to THIS campaign's id;
 *  - CPA (cost per acquired player) and Ad Spend cross the two: `cost / FTDs` and the
 *    ad `cost` itself.
 *
 * Kept free of Redux/effects (no store imports) so it unit-tests directly; the
 * effectful fetching + window maths live in {@link useCampaignPerformance}. Reuses the
 * acquisition `buildStat` / `buildAdSpendStat` so the marker rules (`N/A` failing, `–`
 * divide-by-zero) and the money/delta formatting stay identical to the rest of the app.
 */
import type { Kpi, KpiStat, TopWebsite, CampaignAsset } from '../../mock-data/types';
import type { AssetTone } from '../../theme/tokens';
import type { Bucket } from '../home/stats-shape';
import {
  type AcqKpiSpec,
  type AdSpendColumnInput,
  type AdSpendKpiId,
  buildStat,
  buildAdSpendStat,
} from '../acquisition/performance-stats';
import type {
  AdCampaignAssets,
  AdCampaignNetwork,
  AdPerformanceSite,
} from '../acquisition/api/casino-ad-service';

/* ── windows ────────────────────────────────────────────────────────────────────
 * The grid's two fixed columns: Today (vs yesterday) and This month (vs last month).
 * UTC calendar dates: the `daily_affiliate_activity` aggregation buckets by UTC day
 * (server time, where the live aggregation lives), and `homeWindow`'s TODAY/YESTERDAY
 * are UTC-day too — so the channel-detail card and this page query the SAME calendar
 * day and never diverge for a non-UTC viewer. Stats history windows are end-EXCLUSIVE
 * (end = the day after the last counted day, like `homeWindow`); ad-service windows
 * are INCLUSIVE (`date_from`..`date_to`). */

const pad = (n: number): string => String(n).padStart(2, '0');
const ymd = (d: Date): string => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
/** UTC midnight `dayOffset` days from `nowMs`. */
const midnight = (nowMs: number, dayOffset: number): Date => {
  const d = new Date(nowMs);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + dayOffset));
};

export type DateRange = { from: string; to: string };

export type CampaignWindows = {
  /** Ad-service inclusive ranges (`date_from`..`date_to`). */
  ad: { lifetime: DateRange; today: DateRange; yesterday: DateRange; month: DateRange; lastMonth: DateRange };
  /** Stats history ranges (`start_date`..`end_date`, end-exclusive). */
  stats: { today: DateRange; yesterday: DateRange; month: DateRange; lastMonth: DateRange };
};

/**
 * Resolve the four comparison windows for `nowMs`, plus the campaign's lifetime window
 * (its start day to today) for the hero's total Ad Spend. `startSeconds` is the
 * ad-service `start_date` (epoch SECONDS); when absent the lifetime falls back to a
 * rolling 365-day window so the hero still totals something.
 */
export const computeCampaignWindows = (nowMs: number, startSeconds?: number | null): CampaignWindows => {
  const today = midnight(nowMs, 0);
  const tomorrow = midnight(nowMs, 1);
  const yesterday = midnight(nowMs, -1);
  const monthFirst = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const lastMonthFirst = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
  // Day 0 of this month = the last day of last month.
  const lastMonthLast = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0));
  // Last month truncated to the SAME elapsed length as this month-to-date, so the ad
  // "vs last month" delta compares equal periods (e.g. Jun 1-26 vs May 1-26), not a
  // partial month against a full one. Clamped to last month's length (a 31st maps to,
  // e.g., Feb 28). This mirrors how the stats KPIs' baseline is count-matched
  // (`matchedBaseline` trims last month to the elapsed bucket count).
  const lastMonthMtdEnd = new Date(
    Date.UTC(
      lastMonthFirst.getUTCFullYear(),
      lastMonthFirst.getUTCMonth(),
      Math.min(today.getUTCDate(), lastMonthLast.getUTCDate()),
    ),
  );
  const lifetimeStart = startSeconds ? new Date(startSeconds * 1000) : midnight(nowMs, -365);

  return {
    ad: {
      lifetime: { from: ymd(lifetimeStart), to: ymd(today) },
      today: { from: ymd(today), to: ymd(today) },
      yesterday: { from: ymd(yesterday), to: ymd(yesterday) },
      month: { from: ymd(monthFirst), to: ymd(today) },
      lastMonth: { from: ymd(lastMonthFirst), to: ymd(lastMonthMtdEnd) },
    },
    stats: {
      today: { from: ymd(today), to: ymd(tomorrow) },
      yesterday: { from: ymd(yesterday), to: ymd(today) },
      month: { from: ymd(monthFirst), to: ymd(tomorrow) },
      lastMonth: { from: ymd(lastMonthFirst), to: ymd(monthFirst) },
    },
  };
};

/* ── KPI figures ───────────────────────────────────────────────────────────────── */

/**
 * Per-KPI source for the stats-backed conversion figures, keyed by the running-campaign
 * KPI `id`. Money fields are the scaled integers `buildStat` divides by `AMOUNT_SCALE`;
 * counts plot as-is. CPA + Ad Spend are NOT here — they cross into the ad `cost` and are
 * built with {@link buildAdSpendStat} (see {@link buildCampaignKpi}).
 */
export const CAMPAIGN_STATS_SPECS: Record<string, AcqKpiSpec> = {
  registrations: { kind: 'sum', fields: ['registrations'], money: false },
  ftds: { kind: 'sum', fields: ['ftds'], money: false },
  'ftd-value': { kind: 'ratio', num: ['ftd_total_amount'], den: ['ftds'], money: true },
  turnover: { kind: 'sum', fields: ['casino_bet_amount', 'sport_bet_amount'], money: true },
};

/**
 * One column's inputs: the stats buckets (current + count-matched baseline) for the
 * conversion figures, and the ad `cost` (+ its prior-window baseline) for CPA / Ad
 * Spend. `cost` is `null` until the ad request resolves; `errored` flags a failed
 * stats / cost request (→ `N/A`).
 */
export type CampaignColumn = {
  current: Bucket[];
  baseline: Bucket[];
  statsErrored: boolean;
  cost: number | null;
  baselineCost: number | null;
  costErrored: boolean;
};

/** One running-campaign KPI with both columns sourced from the real data. A KPI `id`
 *  with no known source keeps its chrome (title/subtitle + whatever it carried). */
export const buildCampaignKpi = (kpi: Kpi, left: CampaignColumn, right: CampaignColumn): Kpi => {
  const spec = CAMPAIGN_STATS_SPECS[kpi.id];
  if (spec) {
    return {
      ...kpi,
      today: buildStat(spec, left.current, left.baseline, left.statsErrored),
      month: buildStat(spec, right.current, right.baseline, right.statsErrored),
    };
  }
  if (kpi.id === 'ad-spend' || kpi.id === 'cpa') {
    // Reuse the acquisition ad-spend builder: 'budget-spent' is the cost itself,
    // 'blended-cpa' is `cost / FTDs` (FTDs read from the same stats buckets).
    const adId: AdSpendKpiId = kpi.id === 'ad-spend' ? 'budget-spent' : 'blended-cpa';
    const col = (c: CampaignColumn): AdSpendColumnInput => ({
      cost: c.cost,
      baselineCost: c.baselineCost,
      current: c.current,
      baseline: c.baseline,
      errored: c.costErrored,
    });
    return { ...kpi, today: buildAdSpendStat(adId, col(left)), month: buildAdSpendStat(adId, col(right)) };
  }
  return kpi;
};

/** Override every running-campaign KPI's figures with the real data, leaving the static
 *  title/subtitle untouched. Pure, so it unit-tests directly. */
export const buildCampaignKpis = (chrome: Kpi[], left: CampaignColumn, right: CampaignColumn): Kpi[] =>
  chrome.map((kpi) => buildCampaignKpi(kpi, left, right));

/* ── hero Ad Spend ─────────────────────────────────────────────────────────────── */

/** Whole-currency money string, grouped (matches the KPI Ad Spend formatting). */
export const formatMoney = (value: number): string => `$${Math.round(value).toLocaleString('en-US')}`;

/* ── Top 10 Websites ───────────────────────────────────────────────────────────── */

const formatCount = (n: number): string => Math.round(n).toLocaleString('en-US');

/** Strip protocol / path from a publisher site to a bare domain for the row label. */
const cleanDomain = (site: string): string =>
  site
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .replace(/^www\./i, '');

/** A safe outbound URL for a publisher site (the row links out in a new tab). */
const toSiteUrl = (site: string): string => (/^https?:\/\//i.test(site) ? site : `https://${cleanDomain(site)}`);

/**
 * Map the ad-service `top_sites` (publishers where the campaign ran) to the Top-Websites
 * rows, carrying BOTH the impressions and the clicks the campaign drew from each site.
 * The API returns them already ranked. Rows with no usable `site` string are dropped
 * (a malformed publisher key would otherwise render as a bare/`[object Object]` domain).
 */
export const mapTopSites = (sites: AdPerformanceSite[]): TopWebsite[] =>
  sites
    .filter((site) => typeof site.site === 'string' && site.site.length > 0)
    .map((site, i) => ({
      id: site.site || `site-${i}`,
      name: cleanDomain(site.site),
      url: toSiteUrl(site.site),
      impressions: formatCount(site.impressions),
      clicks: formatCount(site.clicks),
    }));

/* ── creative assets ───────────────────────────────────────────────────────────── */

/** IAB-standard names for the common banner sizes; other sizes fall back to `W×H Banner`. */
const SIZE_NAMES: Record<string, string> = {
  '728x90': 'Leaderboard',
  '300x250': 'Medium Rectangle',
  '160x600': 'Wide Skyscraper',
  '300x600': 'Half Page',
  '320x50': 'Mobile Banner',
  '300x50': 'Mobile Banner',
  '300x100': 'Mobile Banner',
  '320x100': 'Large Mobile Banner',
};

const ASSET_TONES: readonly AssetTone[] = ['amber', 'ember', 'tangerine', 'violet', 'periwinkle', 'royal'];

/** The campaign's network → the asset card's channel label. */
const networkChannel = (network: AdCampaignNetwork): string => {
  switch (network) {
    case 'web':
      return 'Web';
    case 'crypto':
      return 'Crypto';
    case 'app':
    case 'apps':
      return 'Apps';
    default:
      return network ? network.charAt(0).toUpperCase() + network.slice(1) : 'Web';
  }
};

/**
 * Map the ad-service generated banners (keyed by size, with per-size variants) to the
 * creative-asset cards: an IAB name (+ a variant suffix when a size has more than one),
 * the real dimensions driving the wireframe aspect + badge, the campaign network as the
 * channel tag, and a cycled preview tone for visual variety.
 */
export const mapCampaignAssets = (assets: AdCampaignAssets, network: AdCampaignNetwork): CampaignAsset[] => {
  const channel = networkChannel(network);
  const out: CampaignAsset[] = [];
  let i = 0;
  for (const variants of Object.values(assets)) {
    variants.forEach((asset, vi) => {
      const base = SIZE_NAMES[`${asset.width}x${asset.height}`] ?? `${asset.width}×${asset.height} Banner`;
      out.push({
        id: asset.url || `${asset.width}x${asset.height}-${vi}`,
        name: variants.length > 1 ? `${base} · V${vi + 1}` : base,
        type: 'Banner',
        width: asset.width,
        height: asset.height,
        channels: [channel],
        tone: ASSET_TONES[i % ASSET_TONES.length],
        // The real generated banner image — shown in the thumbnail.
        ...(asset.url ? { image: asset.url } : {}),
      });
      i += 1;
    });
  }
  return out;
};
